import React from 'react';
import LeasingInterestModalComponent from '../../edit/components/LeasingInterestModalComponent';
import { LEASING_INTERESTS_LIST_QUERY } from 'features/leasingInterests/list/queries';
import { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { CURRENCIES_DICTIONARY_QUERY } from 'features/common/queries';
import { Card } from '@totalsoft/rocket-ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import LeasingInterestComponent from './LeasingInterestComponent';
import { LEASING_INTEREST_REMOVED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_INTERESTS_READ } from 'constants/rightsCodes';
import useSubscription from 'hooks/apollo/useSubscription';

export const leasingInterestsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'description',
      caption: 'LeasingInterestType.Description',
      type: 'text'
    },
    {
      name: 'currencyId',
      caption: 'LeasingInterestType.Currency',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'isFixed',
      caption: 'LeasingInterestType.Fixed',
      type: 'boolean'
    }
  ],
  orderByFields: [
    {
      caption: 'LeasingInterestType.Id',
      value: 'InterestTypeId'
    },
    {
      caption: 'LeasingInterestType.Description',
      value: 'InterestTypeName'
    }
  ],
  pager: {
    orderBy: 'InterestTypeName',
    direction: 'asc'
  }
});

const LeasingInterestsListBrowser = () => {
  const addToast = useToast();
  const { t } = useTranslation();

  const canRead = usePermission(ADMIN_LEASING_INTERESTS_READ);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: LEASING_INTERESTS_LIST_QUERY,
        name: 'leasingInterests'
      },
      collectionsOptions: [
        {
          queryOptions: { query: CURRENCIES_DICTIONARY_QUERY },
          name: 'currenciesDictionary',
          identifier: 'currencyId',
          collectionType: CollectionType.Filter,
          valueKey: 'id',
          labelKey: 'code'
        }
      ]
    }),
    []
  );

  useSubscription(LEASING_INTEREST_REMOVED, {
    onData: ({ client }) => {
      addToast(t('LeasingInterestType.Removed'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingInterests' });
      client.cache.gc();
    }
  });

  const columns = useMemo(
    () => [
      { name: 'description', caption: 'LeasingInterestType.Description' },
      { name: 'code', caption: 'LeasingInterestType.Code' },
      { name: 'currency', caption: 'LeasingInterestType.Currency' },
      { name: 'periodicity', caption: 'LeasingInterestType.Periodicity' },
      { name: 'isFixed', caption: 'LeasingInterestType.Fixed' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      {canRead && (
        <>
          <TableListBrowser
            name='leasing-interests-browser'
            store={leasingInterestsStore}
            ItemComponent={LeasingInterestComponent}
            columns={columns}
          />
          <ExportModalComponent store={leasingInterestsStore} options={fetchingOptions} />
          <LeasingInterestModalComponent />
          <ApolloFetchingComponent store={leasingInterestsStore} options={fetchingOptions} />
        </>
      )}
    </Card>
  );
};

export default LeasingInterestsListBrowser;
